import { z } from 'zod';
import { CONTENT_LIMITS, HASHTAG_PATTERN } from '../content/content.js';
import { languageTagSchema } from '../language.js';
/**
 * `copywriter` and `translator` (spec 80 §4.7, S80-4). Both write one
 * localization of a content item through module 40 and commit exactly one
 * version based on `base_version_id` — the version the editor saw when the
 * run was requested; a newer version fails the run `content_changed`.
 *
 * Per-platform copy (spec 80 §9 Q3) is not modelled yet: module 40 keys a
 * localization by language only, so each job returns one text per language,
 * written for the strictest platform limits among the item's formats.
 */
/** The language AI copy is written in first (spec 80 §4.8: copywriter source language `de`). */
export const AI_SOURCE_LANGUAGE = 'de';
/** At most this many verified knowledge items go into a copywriter context (`AI_MAX_KNOWLEDGE_ITEMS`, spec 80 §4.5). */
export const AI_MAX_KNOWLEDGE_ITEMS = 40;
/** Content states an AI text job may write into: never `in_review`, `approved` or `archived` content (S80-4 decision). */
export const AI_WRITABLE_CONTENT_STATUSES = ['draft', 'changes_requested'];
/** The member's optional wish for an AI text (E320, spec 80 section 4.20): their own words, steering wording only, never a fact source. */
export const AI_WISH_MAX_LENGTH = 500;
export const AI_COPY_LIMITS = {
    maxClaims: 30,
    maxClaimLength: 500,
    maxClaimSources: 10,
    /** At most this many entries reach one copy context (copywriter v19, E470). */
    maxEntries: 12,
};
const hashtag = z.string().regex(HASHTAG_PATTERN);
/** The texts of one localization as module 40 stores them (spec 40 §4.3); hashtags without `#`. */
export const aiLocalizationTextSchema = z.object({
    headline: z.string().max(CONTENT_LIMITS.headlineMaxLength),
    caption: z.string().min(1).max(CONTENT_LIMITS.captionMaxLength),
    cta: z.string().max(CONTENT_LIMITS.ctaMaxLength),
    hashtags: z.array(hashtag).max(CONTENT_LIMITS.maxHashtags),
});
/**
 * A factual statement of the copy and the verified knowledge items it rests on (REQ-80-014). From copywriter v19
 * (E470) a statement may rest on entries instead (`entry_ids`, data the business maintains); it always cites at
 * least one source.
 */
export const copyClaimSchema = z
    .object({
    text: z.string().min(1).max(AI_COPY_LIMITS.maxClaimLength),
    knowledge_item_ids: z.array(z.string()).max(AI_COPY_LIMITS.maxClaimSources),
    // A default, not optional: the provider schema needs every property; runs before v19 stored none.
    entry_ids: z.array(z.string()).max(AI_COPY_LIMITS.maxClaimSources).default([]),
})
    .refine((claim) => claim.knowledge_item_ids.length + claim.entry_ids.length > 0, {
    message: 'cite at least one knowledge item or entry',
    path: ['knowledge_item_ids'],
});
export const copywriterInputSchema = z.object({
    content_item_id: z.uuid(),
    base_version_id: z.uuid(),
    /** S160-2: the post's own photo; its preview goes to the model with the context (prompt v6 on). */
    photo_asset_id: z.uuid().optional(),
    /** E320 S320-2: the member's wish for "Neu schreiben" (prompt v15 on). */
    wish: z.string().trim().min(1).max(AI_WISH_MAX_LENGTH).optional(),
});
export const copywriterOutputSchema = aiLocalizationTextSchema.extend({
    claims: z.array(copyClaimSchema).max(AI_COPY_LIMITS.maxClaims),
});
export const translatorInputSchema = z.object({
    content_item_id: z.uuid(),
    base_version_id: z.uuid(),
    target_language: languageTagSchema,
});
export const translatorOutputSchema = aiLocalizationTextSchema;
//# sourceMappingURL=copy.js.map